/**
 * Maaş Hareket Servisi — manuel ekleme/kesinti.
 *
 * Puantaj'dan ayrı, yalnızca para hareketleri için (kasa eksiği, hasar bedeli, ekstra ödenek vb.)
 * BOSS + Muhasebe yetkili.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query, where,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import { cokluBildirimGonder, bildirimGonder } from './bildirim-service'
import type { FsTimestamp } from '@/types'

const KOLEKSIYON = 'maasHareketleri'

export type MaasHareketTipi = 'ekleme' | 'kesinti'
export type MaasHareketOlusturanRol = 'boss' | 'yonetici'
export type MaasHareketDurumu = 'onaylandi' | 'bekliyor' | 'reddedildi'

export interface MaasHareketi {
  id: string
  personelId: string
  personelIsim?: string          // denormalize (onay kartı + bildirim için)
  donem: string                  // YYYY-MM
  tip: MaasHareketTipi
  tutar: number                  // pozitif değer (her zaman)
  aciklama: string
  /** BOSS ekler → 'onaylandi' (anında geçerli). Yönetici ekler → 'bekliyor'.
   *  Eski kayıtlarda alan yok → onaylanmış kabul edilir (geriye uyum). */
  durum?: MaasHareketDurumu
  onaylayanId?: string
  onayTarihi?: FsTimestamp
  olusturanId: string
  olusturanRol: MaasHareketOlusturanRol
  olusturmaTarihi: FsTimestamp
}

function yeniId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, KOLEKSIYON)).id
}

export async function maasHareketiOlustur(data: {
  personelId: string
  personelIsim?: string
  donem: string
  tip: MaasHareketTipi
  tutar: number
  aciklama: string
  olusturanId: string
  olusturanRol: MaasHareketOlusturanRol
}): Promise<string> {
  if (data.tutar <= 0) throw new Error('Tutar pozitif olmalı')
  if (!data.aciklama.trim()) throw new Error('Açıklama zorunlu')

  const db = getFirestoreDb()
  const id = yeniId()
  // BOSS eklerse anında geçerli; yönetici eklerse BOSS onayına gider.
  const durum: MaasHareketDurumu = data.olusturanRol === 'boss' ? 'onaylandi' : 'bekliyor'

  await setDoc(doc(db, KOLEKSIYON, id), {
    personelId: data.personelId,
    personelIsim: data.personelIsim ?? '',
    donem: data.donem,
    tip: data.tip,
    tutar: Math.abs(data.tutar),
    aciklama: data.aciklama.trim(),
    durum,
    olusturanId: data.olusturanId,
    olusturanRol: data.olusturanRol,
    olusturmaTarihi: nowServer(),
  })

  // Yönetici eklediyse BOSS'lara onay bildirimi
  if (durum === 'bekliyor') {
    try {
      const bosslar = await bosslariGetir()
      if (bosslar.length > 0) {
        const tipMetni = data.tip === 'ekleme' ? 'ekleme' : 'kesinti'
        await cokluBildirimGonder(bosslar, {
          tipi: 'maasHareketOnay',
          aciliyet: 'normal',
          baslik: '💰 Maaş hareketi onayı bekliyor',
          mesaj: `${data.personelIsim ?? 'Personel'} için ${data.tutar} ₺ ${tipMetni} (${data.aciklama.trim()}) onayına sunuldu.`,
          link: '/boss?maasHareket=' + id,
        })
      }
    } catch (e) {
      console.warn('[maasHareket] onay bildirimi gönderilemedi:', e)
    }
  }

  return id
}

/** Aktif BOSS kullanıcı id'leri */
async function bosslariGetir(): Promise<string[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, 'kullanicilar'), where('rol', '==', 'boss'))
  const snap = await getDocs(q)
  return snap.docs
    .filter((d) => (d.data() as { aktif?: boolean }).aktif !== false)
    .map((d) => d.id)
}

/** Onay bekleyen maaş hareketleri (BOSS feed) */
export async function bekleyenMaasHareketleri(): Promise<MaasHareketi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, KOLEKSIYON), where('durum', '==', 'bekliyor'))
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as MaasHareketi)
    .sort((a, b) => b.olusturmaTarihi.toMillis() - a.olusturmaTarihi.toMillis())
}

/** BOSS onayı → hareket geçerli olur (maaşa yansır) */
export async function maasHareketiOnayla(id: string, onaylayanId: string): Promise<void> {
  const db = getFirestoreDb()
  const s = await getDoc(doc(db, KOLEKSIYON, id))
  if (!s.exists()) throw new Error('Hareket bulunamadı.')
  const h = { id: s.id, ...s.data() } as MaasHareketi
  if (h.durum !== 'bekliyor') throw new Error('Bu hareket zaten sonuçlanmış.')

  await updateDoc(doc(db, KOLEKSIYON, id), {
    durum: 'onaylandi',
    onaylayanId,
    onayTarihi: nowServer(),
  })
  try {
    await bildirimGonder({
      kullaniciId: h.olusturanId,
      tipi: 'maasHareketOnay',
      aciliyet: 'normal',
      baslik: '✅ Maaş hareketi onaylandı',
      mesaj: `${h.personelIsim ?? 'Personel'} için girdiğin ${h.tutar} ₺ ${h.tip} onaylandı.`,
      link: '/yonetici/maaslar',
    })
  } catch (e) {
    console.warn('[maasHareket] onay bildirimi gönderilemedi:', e)
  }
}

/** BOSS reddi → hareket silinir (maaşa yansımaz) */
export async function maasHareketiReddet(id: string, onaylayanId: string): Promise<void> {
  const db = getFirestoreDb()
  const s = await getDoc(doc(db, KOLEKSIYON, id))
  if (!s.exists()) throw new Error('Hareket bulunamadı.')
  const h = { id: s.id, ...s.data() } as MaasHareketi
  if (h.durum !== 'bekliyor') throw new Error('Bu hareket zaten sonuçlanmış.')

  await deleteDoc(doc(db, KOLEKSIYON, id))
  try {
    await bildirimGonder({
      kullaniciId: h.olusturanId,
      tipi: 'maasHareketOnay',
      aciliyet: 'normal',
      baslik: 'ℹ️ Maaş hareketi reddedildi',
      mesaj: `${h.personelIsim ?? 'Personel'} için girdiğin ${h.tutar} ₺ ${h.tip} reddedildi.`,
      link: '/yonetici/maaslar',
    })
  } catch (e) {
    console.warn('[maasHareket] red bildirimi gönderilemedi:', e)
  }
}

/** Personelin tüm hareketleri (opsiyonel dönem filtreli) */
export async function personelHareketleriniGetir(
  personelId: string,
  donem?: string
): Promise<MaasHareketi[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, KOLEKSIYON),
    where('personelId', '==', personelId)
  )
  const snap = await getDocs(q)
  let liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaasHareketi)
  if (donem) liste = liste.filter((h) => h.donem === donem)
  liste.sort((a, b) => b.olusturmaTarihi.toMillis() - a.olusturmaTarihi.toMillis())
  return liste
}

/** Tüm personeller için dönem hareketleri */
export async function donemHareketleriniGetir(donem: string): Promise<MaasHareketi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, KOLEKSIYON), where('donem', '==', donem))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaasHareketi)
  liste.sort((a, b) => b.olusturmaTarihi.toMillis() - a.olusturmaTarihi.toMillis())
  return liste
}

export async function maasHareketiSil(id: string): Promise<void> {
  const db = getFirestoreDb()
  await deleteDoc(doc(db, KOLEKSIYON, id))
}

/** Bir hareket maaşa yansır mı? (onaylı veya eski/durumsuz kayıt) */
export function hareketGecerli(h: MaasHareketi): boolean {
  return h.durum === undefined || h.durum === 'onaylandi'
}

/** Net toplam: ekleme − kesinti (yalnız onaylı/geçerli hareketler) */
export function hareketToplam(hareketler: MaasHareketi[]): number {
  return hareketler.filter(hareketGecerli).reduce((acc, h) => {
    return acc + (h.tip === 'ekleme' ? h.tutar : -h.tutar)
  }, 0)
}
